import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExcelExport, ExcelExportColumn, ExcelExportColumnGroup } from '@progress/kendo-react-excel-export';
import moment from 'moment';
import React, { useRef, useEffect } from 'react';
import { getReportDateFormat } from '@components/central-monitoring/study-summary/study-summary.helper';
import { useTranslation } from 'hooks';
import { convertBooleanToYesNo } from 'utils/convertor';
import { isSplittedValue, validOverviewDate } from '@utils/validation';

export interface ExportExcelComponentInputColumn {
    field: string;
    title?: string;
    label?: string;
    minWidth?: number;
    show?: boolean;
    filter?: string;
    cell?: Function;
    cells?: {
        data: Function;
    };
    children?: this[];
}

interface ExportExcelComponentProps {
    columns: ExportExcelComponentInputColumn[];
    mapColumns: ((columns: ExportExcelComponentInputColumn[]) => ExportExcelComponentInputColumn[]) | null;
    data: any[];
    fileName: string;
    exportTrigger?: number; // use this property to trigger export from the parent component
}

const ExportExcelComponent = ({ columns, mapColumns, data, fileName, exportTrigger }: ExportExcelComponentProps) => {
    const exportRef = useRef<ExcelExport | null>(null);

    const { t } = useTranslation();

    const exportToExcel = () => {
        exportRef.current?.save();
    };

    const exportColumns = mapColumns ? mapColumns(columns) : columns;

    useEffect(() => {
        if (exportTrigger) {
            exportToExcel();
        }
    }, [exportTrigger]);

    const isDateColumn = (column: ExportExcelComponentInputColumn) => {
        if (column.cells?.data) return column.cells.data.name === 'DateCell';

        return column.cell?.name === 'DateCell';
    };

    const isDateTimeColumn = (column: ExportExcelComponentInputColumn) =>
        column.filter === 'date' && !isDateColumn(column);

    const isDateOrDateTimeColumn = (column: ExportExcelComponentInputColumn) => column.filter === 'date';

    const getColumnByFieldName = (fieldName: string) =>
        exportColumns.find((column: ExportExcelComponentInputColumn) => column.field === fieldName);

    const convertFieldString = (value: string, fieldName: string) => {
        const column = getColumnByFieldName(fieldName);

        if (isSplittedValue(value) && validOverviewDate(value)) return value ? getReportDateFormat(value) : value; // Study/Site Overview date display

        if (!column || !isDateOrDateTimeColumn(column)) return value;

        const date = moment.utc(value);

        if (!date.isValid()) return value;

        return isDateColumn(column) ? date.format('DD-MMM-YYYY') : date.format('DD-MMM-YYYY HH:mm:ss');
    };

    const convertFieldValues = (object: { [key: string]: any }, objectName?: string): { [key: string]: any } => {
        return Object.keys(object).reduce(
            (acc, key) => {
                const fieldName = objectName ? `${objectName}.${key}` : key;
                acc[key] = convertFieldValue(object[key], fieldName);
                return acc;
            },
            {} as { [key: string]: any },
        );
    };

    const convertFieldValue = (value: any, fieldName: string) => {
        if (value === null || value === undefined) return '';

        switch (typeof value) {
            case 'boolean':
                return convertBooleanToYesNo(value);
            case 'string':
                return convertFieldString(value, fieldName);
            case 'object':
                return convertFieldValues(value, fieldName);
            default:
                return value;
        }
    };

    function convertDataRow(row: any) {
        return convertFieldValues({ ...row });
    }

    const getData = () => data.map((row: any) => convertDataRow(row));

    const getTitle = (column: any) => {
        let title = column.title ?? column.label ?? column.field;

        if (isDateTimeColumn(column)) title += ' (UTC)';

        return title;
    };

    const excelExportColumns = (expColumns: ExportExcelComponentInputColumn[]) =>
        (expColumns ?? []).map((column: ExportExcelComponentInputColumn) => {
            if (column.children?.length) {
                return (
                    <ExcelExportColumnGroup key={column.field} title={getTitle(column)}>
                        {excelExportColumns(column.children)}
                    </ExcelExportColumnGroup>
                );
            }

            return (
                <ExcelExportColumn
                    key={column.field}
                    field={column.field}
                    title={getTitle(column) || ' '}
                    width={column.minWidth || 180}
                    headerCellOptions={{
                        background: '#fff',
                        color: '#000',
                    }}
                    hidden={column.show === false} // If column.show is falsey, i.e., null or undefined, then column should still show by default
                />
            );
        });

    return (
        <>
            <button
                type="button"
                className="excel-saver"
                onClick={exportToExcel}
                onKeyDown={exportToExcel}
                data-testid="export-to-excel"
            >
                <FontAwesomeIcon className="icon" icon={['fas', 'file-excel']} />
                <span className="excel-wrapper">
                    <p>{t('exportExcel')}</p>
                </span>
            </button>
            <ExcelExport
                ref={exportRef}
                data={getData()}
                fileName={`${fileName.replace(/[/\\?%*:|"<>.]/g, ' ')}.xlsx` ?? ''}
            >
                {excelExportColumns(exportColumns)}
            </ExcelExport>
        </>
    );
};

export default ExportExcelComponent;
